import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// MessageBubble — пузырь одного сообщения в ленте (ядро §0b «Лента сообщений», FR-MSG).
// Ветки (все из props, типы — shared-contracts):
//   • direction in/out: входящие слева, исходящие справа;
//   • grouping single/first/middle/last: «хвостик» (срез радиуса со стороны автора) только у
//     single/last — склейка подряд идущих сообщений одного автора (инв.1, §0b);
//   • время + галочки доставки в нижнем правом углу пузыря (out → DeliveryTicks, D-09);
//   • «изменено» при editedAt (FR-MSG-06);
//   • тумбстоун «Сообщение удалено» при deletedAt — контент НЕ рендерим (его и нет, §4.9);
//   • reply-цитата: цветная вертикальная полоса слева + имя автора оригинала + сниппет (FR-MSG-04),
//     клик → onReplyQuoteClick(messageId);
//   • forward-заголовок «Переслано от {имя}» сверху (FR-MSG-05);
//   • reactions: ряд ReactionPill под пузырём (FR-MSG-08);
//   • markdown-подмножество текста (жирный/курсив/моно/код/ссылки, FR-MSG-02; без unfurl, D-03);
//     упоминания @user подсвечены (FR-MSG-09);
//   • медиа → MediaAttachment/альбом, голос → VoiceMessage, системные → SystemMessage.
// Презентационный: действия наружу — только колбэки. Имя+цвет автора в группе — senderColorKey(userId).
import { useEffect, useRef, useState, } from 'react';
import { senderColorKey } from '../tokens/index.js';
import { formatTime } from '../util/format.js';
import { MarkdownText } from './MarkdownText.js';
import { ReactionPill } from './ReactionPill.js';
import { DeliveryTicks } from './DeliveryTicks.js';
import { MediaAttachment } from './MediaAttachment.js';
import { VoiceMessage } from './VoiceMessage.js';
import { SystemMessage } from './SystemMessage.js';
/** Радиусы углов пузыря: срез под «хвостик» со стороны автора только у single/last. */
function bubbleRadius(direction, grouping) {
    const r = 'var(--radius-bubble)';
    const tail = 'var(--radius-bubble-tail)';
    const hasTail = grouping === 'single' || grouping === 'last';
    if (direction === 'out') {
        // хвостик справа-снизу
        const br = hasTail ? tail : r;
        return `${r} ${r} ${br} ${r}`;
    }
    // in: хвостик слева-снизу
    const bl = hasTail ? tail : r;
    return `${r} ${r} ${r} ${bl}`;
}
export function MessageBubble(props) {
    const { message, direction, senderName, senderColorKeyId, originLabel, grouping = 'single', deliveryState, resolveName, voicePlayback, onReact, onReplyQuoteClick, onOpenContextMenu, onOpenMedia, onDownload, transferOf, onCancelTransfer, onMentionClick, onLinkClick, } = props;
    const bubbleEl = useRef(null);
    // Свайп-ответ (Telegram-паттерн): рефы объявлены ДО ветки system (порядок хуков стабилен).
    const rowEl = useRef(null);
    const swipeIconEl = useRef(null);
    const swipe = useRef(null);
    // Системное сообщение рендерится особым образом — без пузыря/хвостика (edge §4.4).
    if (message.kind === 'system' && message.system) {
        return (_jsx(SystemMessage, { system: message.system, resolveName: resolveName ?? ((id) => String(id)) }));
    }
    const isOut = direction === 'out';
    const showTail = grouping === 'single' || grouping === 'last';
    const showSenderName = !isOut && Boolean(senderName) && (grouping === 'single' || grouping === 'first');
    const deleted = Boolean(message.deletedAt);
    const rowStyle = {
        display: 'flex',
        justifyContent: isOut ? 'flex-end' : 'flex-start',
        padding: `1px var(--space-3)`,
        marginTop: grouping === 'single' || grouping === 'first' ? 'var(--space-2)' : '1px',
        // свайп-ответ: якорь индикатора ↩ + вертикальная прокрутка остаётся браузеру (панорамы по X нет)
        position: 'relative',
        touchAction: 'pan-y',
    };
    const colStyle = {
        display: 'flex',
        flexDirection: 'column',
        alignItems: isOut ? 'flex-end' : 'flex-start',
        maxWidth: 'min(78%, 560px)',
        minWidth: 0,
    };
    const bubbleStyle = {
        position: 'relative',
        padding: 'var(--space-2) var(--space-3)',
        borderRadius: bubbleRadius(direction, grouping),
        background: isOut ? 'var(--color-bubble-out)' : 'var(--color-bubble-in)',
        color: isOut ? 'var(--color-bubble-out-text)' : 'var(--color-bubble-in-text)',
        fontSize: 'var(--font-message)',
        lineHeight: 'var(--line-normal)',
        wordBreak: 'break-word',
        overflowWrap: 'anywhere',
        minWidth: 0,
        // Жёсткая крышка ширины: контент с несжимаемым min-content (nowrap-имя файла в MediaAttachment)
        // раздувал пузырь шире колонки → горизонтальная прокрутка ленты на телефоне (iPhone 03.07).
        // maxWidth прижимает к колонке (она уже ≤ min(78%,560px)), overflow страхует остаточные выбросы,
        // и только с зажатой шириной срабатывает ellipsis имени файла ниже по цепочке.
        maxWidth: '100%',
        overflow: 'hidden',
        boxSizing: 'border-box',
    };
    const onContextMenu = (e) => {
        if (!onOpenContextMenu)
            return;
        e.preventDefault();
        onOpenContextMenu(message.id, { x: e.clientX, y: e.clientY });
    };
    // Long-press на touch (mobile): нет правого клика — держим палец ~450мс → открываем НАШЕ контекст-меню
    // в точке касания. Скролл (touchmove) или отпускание раньше — отмена (обычная прокрутка не ломается).
    // Native-выделение/callout уже подавлены на pointer:coarse (см. ensureGlobalStyles).
    const longPressTimer = useRef(null);
    const touchStart = useRef(null);
    const clearLongPress = () => {
        if (longPressTimer.current !== null) {
            clearTimeout(longPressTimer.current);
            longPressTimer.current = null;
        }
    };
    useEffect(() => clearLongPress, []);
    const onTouchStart = (e) => {
        if (!onOpenContextMenu)
            return;
        const t = e.touches[0];
        if (!t)
            return;
        touchStart.current = { x: t.clientX, y: t.clientY };
        clearLongPress();
        longPressTimer.current = setTimeout(() => {
            longPressTimer.current = null;
            const p = touchStart.current;
            onOpenContextMenu(message.id, { x: p?.x ?? 0, y: p?.y ?? 0 });
        }, 450);
    };
    const onTouchMove = (e) => {
        const t = e.touches[0];
        const s = touchStart.current;
        if (t && s && (Math.abs(t.clientX - s.x) > 10 || Math.abs(t.clientY - s.y) > 10))
            clearLongPress();
    };
    // ── Свайп-ответ (FR-MSG-04, Telegram-паттерн): тянешь сообщение ВЛЕВО → ответ ────────────────
    // Жест на строке (не пузыре) — цель шире. Направление решается по первым ~10px: горизонталь с
    // dx<0 → «свайпаем» (строка едет за пальцем до -80px, справа проявляется ↩); вертикаль → жест
    // отдан прокрутке (touch-action: pan-y — браузер сам не панорамирует по X, preventDefault не
    // нужен). Отпустили за порогом −56px → onReply. Трансформ — прямой DOM (без setState на каждый
    // touchmove). Тумбстоун/без-onReply — жест выключен.
    const SWIPE_TRIGGER_PX = -56;
    const SWIPE_MAX_PX = -80;
    const swipeEnabled = Boolean(props.onReply) && !deleted;
    const onRowTouchStart = (e) => {
        if (!swipeEnabled)
            return;
        const t = e.touches[0];
        if (!t)
            return;
        swipe.current = { x: t.clientX, y: t.clientY, mode: 'undecided', dx: 0 };
    };
    const onRowTouchMove = (e) => {
        const s = swipe.current;
        const t = e.touches[0];
        if (!s || !t)
            return;
        const dx = t.clientX - s.x;
        const dy = t.clientY - s.y;
        if (s.mode === 'undecided') {
            if (Math.abs(dx) < 10 && Math.abs(dy) < 10)
                return;
            s.mode = dx < 0 && Math.abs(dx) > Math.abs(dy) * 1.4 ? 'h' : 'v';
            if (s.mode === 'h')
                clearLongPress(); // жест — не long-press
        }
        if (s.mode !== 'h')
            return;
        s.dx = Math.max(SWIPE_MAX_PX, Math.min(0, dx));
        const row = rowEl.current;
        if (row !== null) {
            row.style.transition = '';
            row.style.transform = `translateX(${s.dx}px)`;
        }
        const icon = swipeIconEl.current;
        if (icon !== null)
            icon.style.opacity = String(Math.min(1, s.dx / SWIPE_TRIGGER_PX));
    };
    const endSwipe = () => {
        const s = swipe.current;
        swipe.current = null;
        if (s === null || s.mode !== 'h')
            return;
        const row = rowEl.current;
        if (row !== null) {
            row.style.transition = 'transform .18s ease';
            row.style.transform = '';
        }
        const icon = swipeIconEl.current;
        if (icon !== null)
            icon.style.opacity = '0';
        if (s.dx <= SWIPE_TRIGGER_PX)
            props.onReply?.(message.id);
    };
    const cancelSwipe = () => {
        const s = swipe.current;
        swipe.current = null;
        if (s === null || s.mode !== 'h')
            return;
        const row = rowEl.current;
        if (row !== null) {
            row.style.transition = 'transform .18s ease';
            row.style.transform = '';
        }
        const icon = swipeIconEl.current;
        if (icon !== null)
            icon.style.opacity = '0';
    };
    // — содержимое пузыря —
    let body;
    if (deleted) {
        body = (_jsx("span", { "data-tombstone": "", style: { fontStyle: 'italic', color: 'var(--color-text-muted)' }, children: "\u0421\u043E\u043E\u0431\u0449\u0435\u043D\u0438\u0435 \u0443\u0434\u0430\u043B\u0435\u043D\u043E" }));
    }
    else {
        body = (_jsxs(_Fragment, { children: [message.forwardedFrom ? (_jsxs("div", { "data-forward-header": "", style: {
                        fontSize: 'var(--font-xs)',
                        color: 'var(--color-accent)',
                        fontWeight: 'var(--weight-medium)',
                        marginBottom: '2px',
                    }, children: ["\u041F\u0435\u0440\u0435\u0441\u043B\u0430\u043D\u043E \u043E\u0442 ", message.forwardedFrom.originalSenderName] })) : null, showSenderName ? (_jsx("div", { "data-sender-name": "", style: {
                        fontSize: 'var(--font-sm)',
                        fontWeight: 'var(--weight-semibold)',
                        color: senderColorKey(senderColorKeyId ?? message.senderId),
                        marginBottom: '2px',
                    }, children: senderName })) : null, message.replyTo ? (_jsx("button", { type: "button", "data-reply-quote": "", onClick: () => onReplyQuoteClick?.(message.replyTo.messageId), style: {
                        display: 'block',
                        textAlign: 'left',
                        width: '100%',
                        border: 'none',
                        background: 'transparent',
                        padding: '2px 0 2px 8px',
                        marginBottom: '4px',
                        borderLeft: `3px solid ${senderColorKey(message.replyTo.senderId)}`,
                        cursor: 'pointer',
                        minWidth: 0,
                    }, children: _jsx("span", { "data-reply-snippet": "", style: {
                            display: 'block',
                            fontSize: 'var(--font-xs)',
                            color: 'var(--color-text-muted)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }, children: message.replyTo.preview }) })) : null, message.kind === 'voice' && message.voice ? (_jsx(VoiceMessage, { voice: message.voice, isPlaying: voicePlayback?.isPlaying ?? false, currentTimeMs: voicePlayback?.currentTimeMs ?? 0, speed: voicePlayback?.speed ?? 1, transcriptOpen: voicePlayback?.transcriptOpen ?? false, onPlayPause: voicePlayback?.onPlayPause ?? (() => { }), onSeek: voicePlayback?.onSeek ?? (() => { }), onSpeed: voicePlayback?.onSpeed ?? (() => { }), onToggleTranscript: voicePlayback?.onToggleTranscript })) : null, message.kind === 'media' && message.attachments && message.attachments.length > 0 ? (_jsx(MediaAttachment, { attachments: message.attachments, onOpen: (i) => onOpenMedia?.(message.id, i), onDownload: onDownload ? (i) => onDownload(message.id, i) : undefined, transferOf: transferOf, onCancelTransfer: onCancelTransfer ? (i) => onCancelTransfer(message.id, i) : undefined })) : null, message.text ? (_jsx("span", { "data-text": "", style: { display: 'block' }, children: _jsx(ClampedText, { text: message.text, onMentionClick: onMentionClick, onLinkClick: onLinkClick }) })) : null] }));
    }
    return (_jsxs("div", { ref: rowEl, "data-message-row": "", "data-direction": direction, style: rowStyle, onTouchStart: onRowTouchStart, onTouchMove: onRowTouchMove, onTouchEnd: endSwipe, onTouchCancel: cancelSwipe, children: [swipeEnabled ? (_jsx("span", { ref: swipeIconEl, "data-swipe-reply-icon": "", "aria-hidden": "true", style: {
                    position: 'absolute',
                    right: 'calc(-1 * var(--space-3))',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    width: 28,
                    height: 28,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '50%',
                    background: 'var(--color-surface-hover)',
                    color: 'var(--color-text-muted)',
                    fontSize: 'var(--font-md)',
                    opacity: 0,
                    pointerEvents: 'none',
                }, children: "\u21A9" })) : null, _jsxs("div", { style: colStyle, children: [_jsxs("div", { ref: bubbleEl, "data-message-bubble": "", "data-grouping": grouping, "data-tail": showTail ? '' : undefined, "data-kind": message.kind, "data-deleted": deleted ? '' : undefined, style: bubbleStyle, onContextMenu: onContextMenu, onTouchStart: onTouchStart, onTouchMove: onTouchMove, onTouchEnd: clearLongPress, onTouchCancel: clearLongPress, children: [body, _jsxs("span", { "data-meta": "", style: {
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    float: 'right',
                                    marginLeft: 'var(--space-3)',
                                    marginTop: '2px',
                                    fontSize: 'var(--font-xs)',
                                    color: isOut ? 'var(--color-bubble-out-text)' : 'var(--color-text-muted)',
                                    opacity: 0.75,
                                    userSelect: 'none',
                                }, children: [originLabel !== undefined && originLabel.length > 0 && !deleted ? (_jsx("span", { "data-origin-label": "", children: originLabel })) : null, message.editedAt && !deleted ? _jsx("span", { "data-edited": "", children: "\u0438\u0437\u043C\u0435\u043D\u0435\u043D\u043E" }) : null, _jsx("span", { "data-time": "", children: formatTime(message.createdAt) }), isOut && deliveryState && !deleted ? (_jsx(DeliveryTicks, { state: deliveryState, size: 15 })) : null] })] }), !deleted && message.reactions && message.reactions.length > 0 ? (_jsx("div", { "data-reactions": "", style: {
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '4px',
                            marginTop: '4px',
                            justifyContent: isOut ? 'flex-end' : 'flex-start',
                        }, children: message.reactions.map((r) => (_jsx(ReactionPill, { reaction: r, onToggle: (emoji) => onReact?.(message.id, emoji) }, r.emoji))) })) : null] })] }));
}
// ── ClampedText — защита рендера от гигантских текстов ─────────────────────────────────────────────
// Живой случай: 11-МБ CSV одним сообщением ронял WebKit на iPhone («повторно возникла проблема») —
// markdown-парсер и раскладка на мегабайтной строке взрывают память. Показываем безопасный префикс,
// остальное — по явному запросу порциями. Обрезка не рвёт суррогатную пару (эмодзи на границе).
const TEXT_CLAMP = 4096;
const TEXT_CLAMP_STEP = 16_384;
function safeSlice(text, end) {
    // не резать посреди суррогатной пары
    const code = text.charCodeAt(end - 1);
    return text.slice(0, code >= 0xd800 && code <= 0xdbff ? end - 1 : end);
}
function ClampedText({ text, onMentionClick, onLinkClick, }) {
    const [shown, setShown] = useState(TEXT_CLAMP);
    if (text.length <= shown) {
        return _jsx(MarkdownText, { text: text, onMentionClick: onMentionClick, onLinkClick: onLinkClick });
    }
    const hidden = text.length - shown;
    return (_jsxs(_Fragment, { children: [_jsx(MarkdownText, { text: safeSlice(text, shown), onMentionClick: onMentionClick, onLinkClick: onLinkClick }), _jsxs("button", { type: "button", "data-show-more": "", onClick: () => setShown((n) => n + TEXT_CLAMP_STEP), style: {
                    display: 'block',
                    marginTop: '4px',
                    border: 'none',
                    background: 'transparent',
                    color: 'var(--color-accent)',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: 'var(--font-sm)',
                }, children: ["\u041F\u043E\u043A\u0430\u0437\u0430\u0442\u044C \u0435\u0449\u0451 (\u0441\u043A\u0440\u044B\u0442\u043E ", hidden.toLocaleString('ru-RU'), " \u0441\u0438\u043C\u0432.)"] })] }));
}
//# sourceMappingURL=MessageBubble.js.map